/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    DoCheck,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { LoggerService } from '../../shared/services/logger.service';
import { ReportDefinition } from '../state/report.model';
import { Select, Store } from '@ngxs/store';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { isEqual } from 'lodash-es';
import {
    concatMap,
    delay,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
} from 'rxjs/operators';
import { ReportDataSource } from './datasource/report.datasource';
import { DataFormService } from '../services/dataform.service';
import { ReportService } from '../services/report.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ReportState } from '../state/report.state';
import { LoadDataForm } from '../state/dataForm.actions';
import { DataFormDialogComponent } from '../dataForm/data-form-dialog.component';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DataFormField, DataFormLaunchOptions, DataFormModel, DataFormStateModel } from '../state/dataForm.model';
import { ReportButtonComponent } from '../../../elements/reportButtons/report-button.component';
import { IsolatedReportService } from '../services/isolatedReport.service';
import { PdfDialogComponent } from '../../../core/ui/pdf/pdf-dialog.component';
import { DeviceService } from '../../shared/services/device.service';
import { ClearReportOptions, SetPagingOptions } from '../state/report.actions';
import { PdfPrintResult } from '../../map/command/print/state/print-options.model';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { PageState } from '../../../core/state/page/page.state';
import { PagingOptions } from '../../../types';

@Component({
    selector: 'dash-gis-report-content-container',
    templateUrl: './report-content-container.component.html',
    styleUrls: ['./report-content-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ReportContentContainerComponent implements AfterContentInit, DoCheck, OnChanges, OnDestroy, OnInit {
    @Input() public definition: ReportDefinition;
    @Input() public keys: any[];
    @Input() public print = false;

    @Output() public recordCount = new EventEmitter<number>();

    public dataSource: ReportDataSource;

    @ViewChild(MatSort) sort: MatSort;

    @ContentChildren('dash-gis-report-button', {})
    reportButtons: QueryList<ReportButtonComponent>;

    // table edit
    tableEditFormGroup = new UntypedFormGroup({});
    tableEditActive = false;
    reportSubType$: Observable<string>;
    tableDataForm: DataFormModel;

    private disposeSubject = new Subject<void>();
    private dispose$ = this.disposeSubject.asObservable();
    private initializedSubject = new BehaviorSubject<boolean>(false);

    private lastClientRect: DOMRect;

    private datasourceCreatedSubject = new Subject<void>();

    @Select(ReportState.tableEditEnabled) private tableEdit$: Observable<(id: string, reportName: string) => boolean>;

    constructor(
        protected logger: LoggerService,
        protected ngZone: NgZone,
        protected sanitizer: DomSanitizer,
        protected reportService: ReportService,
        protected isolatedReport: IsolatedReportService,
        protected dataFormService: DataFormService,
        protected deviceService: DeviceService,
        protected dialog: MatDialog,
        protected store: Store,
        protected el: ElementRef,
        protected fb: UntypedFormBuilder,
        protected cd: ChangeDetectorRef
    ) {
        this.datasourceCreatedSubject.asObservable().subscribe(() => {
            this.reportSubType$ = this.getReportSubType$();

            this.isolatedReport.dataSource = this.dataSource;

            if (this.print || this.definition.subType === 'chart' || this.definition.subType === 'label') {
                this.isolatedReport.allDataTrigger$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
                    this.dataSource.loadAllReportData();
                });

                this.dataSource.reportPdf$.pipe(takeUntil(this.dataSource.disposing$)).subscribe((pdfDataUri) => {
                    this.openPdfDialog(pdfDataUri);
                });
            } else {
                this.dataSource.reportData$
                    .pipe(
                        takeUntil(this.dataSource.disposing$),
                        filter((data) => data !== null)
                    )
                    .subscribe((data) => {
                        this.isolatedReport.setHasData(data.length > 0);
                        if (
                            data.length > 0 &&
                            this.definition.type === 'data' &&
                            this.definition.subType === 'detail'
                        ) {
                            this.isolatedReport.objectKey = data[0][this.definition.objectKey];
                        }
                        this.isolatedReport.markChanged();
                    });

                this.dataSource.count$.pipe(takeUntil(this.dataSource.disposing$)).subscribe((count) => {
                    this.recordCount.emit(count);
                });

                this.dataSource.reportHtml$
                    .pipe(
                        distinctUntilChanged(),
                        takeUntil(this.dispose$),
                        filter((html: SafeHtml) => !!html),
                        delay(100)
                    )
                    .subscribe(() => {
                        this.isolatedReport.setIsVolt(true);

                        // add event listeners for report button (custom elements)
                        const buttons = (this.el.nativeElement as HTMLElement).getElementsByTagName('dash-gis-report-button');
                        for (let i = 0; i < buttons.length; i++) {
                            const button = buttons[i];
                            button.addEventListener('launch', (e: CustomEvent) => {
                                this.reportService.runReportByName(e.detail);
                            });
                        }
                        const editButtons = (this.el.nativeElement as HTMLElement).getElementsByTagName(
                            'dash-gis-report-edit-button'
                        );
                        for (let i = 0; i < editButtons.length; i++) {
                            const editButton = editButtons[i];
                            editButton.addEventListener('saved', () => {
                                this.isolatedReport.triggerReport();
                            });
                        }

                        const imageDeleteButtons = (this.el.nativeElement as HTMLElement).getElementsByTagName(
                            'dash-gis-file-download'
                        );
                        for (let i = 0; i < imageDeleteButtons.length; i++) {
                            const imageDeleteButton = imageDeleteButtons[i];
                            imageDeleteButton.addEventListener('changed', () => {
                                this.isolatedReport.triggerReport();
                            });
                        }
                    });

                this.isolatedReport.excelDownloadTrigger$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
                    this.downloadExcel();
                });

                this.initTableEdit();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.definition && changes.definition.currentValue) {
            if (!isEqual(changes.definition.currentValue, changes.definition.previousValue)) {
                // check if options exist for this report
                const options = this.store.selectSnapshot<(id: string, reportName: string) => PagingOptions>(
                    ReportState.paging
                )(this.id, this.definition.name);
                if (!options) {
                    this.store.dispatch(
                        new SetPagingOptions({
                            id: this.id,
                            reportName: this.definition.name,
                            pagingOptions: {
                                page: 0,
                            },
                        })
                    );
                }

                if (this.dataSource) {
                    this.dataSource.dispose();
                    this.dataSource = null;
                    if (changes.definition.previousValue) {
                        this.store.dispatch(
                            new ClearReportOptions({
                                id: this.id,
                                reportName: changes.definition.previousValue.name,
                            })
                        );
                    }
                }
                this.dataSource = new ReportDataSource(
                    this.logger,
                    this.ngZone,
                    this.store,
                    this.sanitizer,
                    this.reportService,
                    this.isolatedReport,
                    this.print,
                    this.definition,
                    this.keys
                );
                this.datasourceCreatedSubject.next();
            }
        }

        if (changes.keys && changes.keys.currentValue) {
            if (!isEqual(changes.keys.currentValue, changes.keys.previousValue)) {
                this.dataSource.keys = changes.keys.currentValue;
                // this.dataSource.loadReportData();
            }
        }
    }

    ngDoCheck(): void {
        const dims = this.el.nativeElement.getBoundingClientRect();
        if (!DashGisUtil.compareRect(dims, this.lastClientRect)) {
            // if (this.definition?.chartConfig?.legendPosition === 'below') {
            //     const legend = this.el.nativeElement.querySelector('.chart-legend');
            //     if (legend) {
            //         dims.height -= legend.getBoundingClientRect().height;
            //     }
            // }
            this.isolatedReport.setContentDimension([dims.width, dims.height]);
            this.lastClientRect = dims;
        }
    }

    ngOnInit() {
        this.initializedSubject.next(true);
    }

    ngAfterContentInit() {}

    ngOnDestroy() {
        this.disposeSubject.next();
        if (this.dataSource) {
            this.dataSource.dispose();
            this.dataSource = null;
        }
    }

    public onEditRowData(evt: DataFormLaunchOptions) {
        this.store
            .dispatch(
                new LoadDataForm({
                    name: evt.name,
                    keys: evt.keys,
                    initialSelection: null,
                    preLaunchCallback: null,
                    postSaveCallback: null,
                })
            )
            .pipe(switchMap(() => this.dialog.open(DataFormDialogComponent, { data: evt.name }).afterClosed()))
            .subscribe(() => {
                this.isolatedReport.triggerReport();
            });
    }

    getDataFieldDefinition(fieldName: string): DataFormField {
        return this.tableDataForm.definition.fields.find((field) => field.fieldName === fieldName);
    }

    getDataFieldValue(fieldName: string, key: any): any {
        return this.tableDataForm.definition.rows[key][fieldName];
    }

    private initTableEdit() {
        this.isolatedReport.enableTableEdit$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
            this.tableEditActive = true;
        });

        const tableEdit$ = this.tableEdit$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            takeUntil(this.dispose$)
        );
        tableEdit$.subscribe((tableEditActive) => {
            this.tableEditActive = tableEditActive;
            this.cd.markForCheck();
        });

        tableEdit$
            .pipe(
                filter((b) => b),
                switchMapTo(this.dataSource.reportData$),
                map((data) => data.map((row) => row[this.definition.dbKey])),
                distinctUntilChanged(isEqual),
                concatMap((keys) =>
                    this.store.dispatch(
                        new LoadDataForm({
                            keys,
                            name: this.definition.tableDataFormName,
                        })
                    )
                )
            )
            .subscribe((state) => {
                const dataFormState: DataFormStateModel = state.dataForm;
                this.tableDataForm = dataFormState.definitions[this.definition.tableDataFormName];
                // build formGroup
                this.tableDataForm.keys.forEach((key) => {
                    const rowGroup = new UntypedFormGroup({});
                    this.definition.fields.forEach((reportField) => {
                        const df = this.getDataFieldDefinition(reportField.name);
                        if (df) {
                            const value = this.getDataFieldValue(reportField.name, key);
                            const name = reportField.name;
                            rowGroup.addControl(name, new UntypedFormControl(value));
                        }
                    });
                    this.tableEditFormGroup.addControl(key, rowGroup);
                });
            });
    }

    private downloadExcel(): void {
        this.dataSource.clearAllReportData();

        const fileName = this.definition.displayName.replace(/ /g, '_');
        this.dataSource.reportAllRawData$
            .pipe(
                filter((data) => data !== null),
                take(1)
            )
            .subscribe((reportData) => {
                const excelData = this.reportService.prepareExcelData(this.definition, reportData);
                this.reportService.downloadExcel(excelData, fileName);
            });
        this.dataSource.loadAllReportData();
    }

    private openPdfDialog(pdfDataUri: string) {
        const filename = this.definition.displayName.replace(/ /g, '_').toLocaleLowerCase();
        const blob = DashGisUtil.dataUriToBlob(pdfDataUri);
        const pdfConfig: MatDialogConfig<PdfPrintResult> = {
            data: {
                blob,
                isBlob: true,
                filename,
            },
        };

        pdfConfig.width = '80vw';
        pdfConfig.height = '80vh';
        pdfConfig.panelClass = 'print-pdf-container';
        this.dialog.open(PdfDialogComponent, pdfConfig);
    }

    private getReportSubType$(): Observable<string> {
        return this.dataSource.getReportSubType$();
    }

    get id(): string {
        return this.isolatedReport.id;
    }

    set id(value: string) {
        this.isolatedReport.id = value;
    }

    get initialized$() {
        return this.initializedSubject.asObservable();
    }

    get mapEnabled() {
        return this.store.selectSnapshot(PageState.mapEnabled);
    }
}
